import React, { useState } from 'react'
import _ from 'lodash'
import { Button, Flex, Modal, Spin, Tooltip, Typography } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { useTranslation } from 'react-i18next'

import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useGetDataOnScroll } from '../../../../components/getDataOnScrollProps/useGetDataOnScrollProps'
import { EmptyData } from '../../../../components/empty/emptyData'
import { PageHeader } from '../../../../components/page-header'
import { ContentPartFilter } from '../../../../components/content-part/ContentPartFilter'
import { AddStockModal } from './AddStock'
import { FilterStock } from './StockFilter'
import { useStock } from './useStock'
import { useLazyGetStockListQuery } from '../../../../redux/api/chemicalization/stockApi'
import { EntStockFilter } from '../../../../models/filters/stockFilter'
import { EntStock } from '../../../../models/chemicalization/stock'
import i18n from 'i18next'

enum FormField {
	warehouseId,
	reportingYear,
	fertilizerIds,
	companyId,
}

export const Stock = () => {
	const [modal, contextHolder] = Modal.useModal()
	const [selectedItem, setSelectedItem] = useState<EntStock>(new EntStock())
	const [showModal, setShowModal] = useState<boolean>(false)
	const [form] = useForm()
	const { t } = useTranslation()
	const { onAddStock, onDeleteStock, onEditStock } = useStock(modal, () => {
		searchHandleClick()
	})
	const [height, setHeight] = useState<number>(0)

	const [getArchiveFolders] = useLazyGetStockListQuery()

	const {
		filter,
		fetching,
		data,
		access,
		totalItems,
		setData,
		setFetching,
		setFilter,
		scrollHandler,
		setCurrentPage,
	} = useGetDataOnScroll<EntStock, EntStockFilter>({
		initialFilter: new EntStockFilter(),
		query: getArchiveFolders,
	})
	const openAddModal = (item: EntStock) => {
		setSelectedItem(item)
		setShowModal(true)
	}
	const setEntityFromFields = (): EntStockFilter => {
		let filt: EntStockFilter = _.clone(filter)
		filt.warehouseId = form.getFieldValue(FormField.warehouseId) ?? null
		filt.companyId = form.getFieldValue(FormField.companyId) ?? null
		filt.reportingYear = form.getFieldValue(FormField.reportingYear) ?? null
		filt.fertilizerIds = form.getFieldValue(FormField.fertilizerIds) ?? null
		filt.page = 1
		return filt
	}
	const clearFields = () => {
		form.setFields([
			{ name: FormField.warehouseId, value: null },
			{ name: FormField.companyId, value: null },
			{ name: FormField.reportingYear, value: null },
			{ name: FormField.fertilizerIds, value: null },
		])
	}

	const searchHandleClick = () => {
		setFilter(setEntityFromFields())
		setData([])
		setCurrentPage(0)
		setFetching(true)
	}

	const table = (
		<div style={{ maxHeight: `${height}px`, overflow: 'auto' }} onScroll={scrollHandler}>
			<table>
				<thead>
					<tr>
						<th style={{ width: '40px' }}>#</th>
						<th>{t('chemicalization.stock.company_name')}</th>
						<th>{t('chemicalization.stock.warehouse_name')}</th>
						<th>{t('titles.types_of_fertilizers')}</th>
						<th>{t('titles.fertilizer_category')}</th>
						<th>{t('chemicalization.stock.price_per_kg')}</th>
						<th>{t('chemicalization.stock.available_quantity')}</th>
						<th>{t('chemicalization.stock.distributed_quantity')}</th>
						<th>{t('chemicalization.stock.total_quantity')}</th>
						<th>{t('chemicalization.stock.reporting_year')}</th>
						<th style={{ width: '120px' }}></th>
					</tr>
				</thead>
				<tbody>
					{fetching ? (
						<tr>
							<th colSpan={11}>
								<Spin
									spinning={fetching}
									style={{
										width: '100%',
										height: '180px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								/>
							</th>
						</tr>
					) : null}
					{data.length !== 0 ? (
						data.map((item, index) => (
							<tr key={item.id}>
								<td>{++index}</td>
								<td>{item?.company?.companyName}</td>
								<td>{item?.warehouseName}</td>
								<td>
									{i18n.language === 'ru' ? item.fertilizer?.nameRu : item.fertilizer?.nameKy}
								</td>
								<td>
									{i18n.language === 'ru'
										? item.fertilizer?.fertilizerCategory?.nameRu
										: item.fertilizer?.fertilizerCategory?.nameKy}
								</td>
								<td>{item?.pricePerKg}</td>
								<td>{item?.availableQuantity}</td>
								<td>{item?.distributedQuantity}</td>
								<td>{item?.totalQuantity}</td>
								<td>{item?.reportingYear}</td>

								<td>
									<Flex gap='small'>
										{access && access.methodPut && (
											<Tooltip title={t('general.edit')}>
												<Button
													className='btn_edit'
													onClick={() => {
														openAddModal(item)
													}}
													type='primary'
													shape='circle'
													icon={<EditOutlined />}
												/>
											</Tooltip>
										)}
										{access && access.methodDelete && (
											<Tooltip title={t('general.delete')}>
												<Button
													className='btn_edit'
													danger
													onClick={() => {
														onDeleteStock(item)
													}}
													type='primary'
													shape='circle'
													icon={<DeleteOutlined />}
												/>
											</Tooltip>
										)}
									</Flex>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan={11}>
								<EmptyData />
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	)

	return (
		<>
			<PageHeader
				title={t('titles.stock')}
				additionalInfo={
					<Typography.Text style={{ marginBottom: '0.5em', display: 'block' }} type='secondary'>
						{t('chemicalization.stock.stock_desc')} {t('general.records_found')} {totalItems}
					</Typography.Text>
				}
			/>
			{access && access.methodPost && (
				<Button
					type='primary'
					icon={<PlusOutlined />}
					onClick={() => {
						openAddModal(new EntStock())
					}}
				>
					{t('general.add')}
				</Button>
			)}
			<ContentPartFilter
				filter={
					<FilterStock
						onSearch={searchHandleClick}
						onReset={() => {
							clearFields()
							searchHandleClick()
						}}
						formField={FormField}
						form={form}
					/>
				}
				setHeight={setHeight}
				table={table}
			/>

			<AddStockModal
				stock={selectedItem}
				show={showModal}
				onHide={() => {
					setShowModal(false)
				}}
				onSubmit={selectedItem.id !== null ? onEditStock : onAddStock}
			/>
			{contextHolder}
		</>
	)
}
